import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMenu } from '../services/front_api';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import AppCombo from './AppCombo';
import { ACTIVE_APP_CHANGED_EVENT, setActiveApp } from '../services/apps';

const menuStyles = {
    container: {
        padding: '4px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: '100%',
    },
    sectionLabel: {
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--text-secondary)',
        padding: '12px 8px 4px',
        margin: 0,
    },
    loading: {
        padding: '16px 8px',
        fontSize: '13px',
        color: 'var(--text-secondary)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    error: {
        padding: '12px',
        fontSize: '13px',
        color: '#ef4444',
        background: 'rgba(239, 68, 68, 0.08)',
        borderRadius: '8px',
        margin: '8px 0',
    },
    treeWrapper: {
        flex: 1,
        overflowY: 'auto',
    },
};

const buildTree = (items, onItemClick) => {
    if (!items || !items.length) return null;
    const sorted = [...items].sort((a, b) => a.rank - b.rank);
    return sorted.map((item) => {
        const isLeaf = !item.Children || item.Children.length === 0;
        return (
            <TreeItem
                key={item.id}
                itemId={String(item.id)}
                label={item.label}
                onClick={isLeaf ? () => onItemClick(item) : undefined}
                sx={{
                    '& .MuiTreeItem-content': {
                        borderRadius: '8px',
                        padding: '4px 8px',
                        margin: '1px 0',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            backgroundColor: 'var(--menu-hover)',
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'var(--menu-active)',
                            '&:hover': {
                                backgroundColor: 'var(--menu-active)',
                            },
                        },
                    },
                    '& .MuiTreeItem-label': {
                        fontSize: '13px',
                        fontWeight: 400,
                        color: 'var(--text-color)',
                        padding: '2px 0',
                    },
                    '& .MuiTreeItem-iconContainer': {
                        color: 'var(--text-secondary)',
                    },
                }}
            >
                {isLeaf ? null : buildTree(item.Children, onItemClick)}
            </TreeItem>
        );
    });
};

const DynamicMenu = () => {
    const navigate = useNavigate();
    const [selectedApp, setSelectedApp] = useState(() => localStorage.getItem('active_app') || '');
    const [menu, setMenu] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleItemClick = (item) => {
        if (item.url) {
            navigate(item.url);
        } else {
            navigate(`/chart/page/${item.id}`);
        }
    };

    useEffect(() => {
        if (!selectedApp) {
            setMenu([]);
            return;
        }

        const fetchMenu = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getMenu(selectedApp);
                setMenu(data);
            } catch (err) {
                setError('Erreur lors de la récupération du menu');
                setMenu([]);
                navigate(`/logout`);
            } finally {
                setLoading(false);
            }
        };

        fetchMenu();
    }, [selectedApp]);

    useEffect(() => {
        const handleActiveAppChanged = (event) => {
            setSelectedApp(event.detail?.appId || localStorage.getItem('active_app') || '');
        };

        window.addEventListener(ACTIVE_APP_CHANGED_EVENT, handleActiveAppChanged);

        return () => {
            window.removeEventListener(ACTIVE_APP_CHANGED_EVENT, handleActiveAppChanged);
        };
    }, []);

    const handleAppChange = (event) => {
        const app = event.target.value;
        setActiveApp(app);
    };

    return (
        <div style={menuStyles.container}>
            <p style={menuStyles.sectionLabel}>Application</p>
            <AppCombo onChange={handleAppChange} value={selectedApp} />
            {loading && <div style={menuStyles.loading}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                Chargement...
            </div>}
            {error && <div style={menuStyles.error}>{error}</div>}
            {!loading && !error && menu.length > 0 && (
                <>
                    <p style={menuStyles.sectionLabel}>Navigation</p>
                    <div style={menuStyles.treeWrapper}>
                        <SimpleTreeView
                            sx={{
                                '& .MuiTreeItem-root': {
                                    '& .MuiCollapse-root': {
                                        marginLeft: '12px',
                                        borderLeft: '1px solid var(--border-color)',
                                        paddingLeft: '4px',
                                    },
                                },
                            }}
                        >
                            {buildTree(menu, handleItemClick)}
                        </SimpleTreeView>
                    </div>
                </>
            )}
        </div>
    );
};

export default DynamicMenu;
